import React, { createRef, useEffect, useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import useShareHolders from "./useShareHolders";
import Notification from "../../components/entities/notification/Notification";
import AdminOnly from "../AdminOnly";
import { Menu } from "primereact/menu";
import { Button } from "primereact/button";
import AuditSidebar from "../audit-sidebar/AuditSidebar";
import API_ROUTES from "../../api-routes";
import { Link, useNavigate } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import { format } from "date-fns";
import { currencyList } from "~/utils/currency";

const ShareHoldersTable = ({
  onAddShareholderClick,
  successMessage,
  setSuccessMessage,
  editToastMsg,
  setEditToastMsg,
}) => {
  const { data, entityId } = useShareHolders();
  const contextMenu = createRef();
  const [isAuditSidebarOpen, setIsAuditSidebarOpen] = useState(false);
  const [selectedShareHolder, setSelectedShareHolder] = useState(null);
  const [sortedData, setSortedData] = useState([]);
  const [sortMeta, setSortMeta] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const getFullName = (shareholder) =>
    [shareholder?.name, shareholder?.other_name, shareholder?.last_name]
      ?.filter(Boolean)
      .join(" ")
      .trim()
      .toLowerCase() ||
    shareholder?.legal_name?.toLowerCase() ||
    "";

  const filteredData = (data || []).filter((row) => {
    const fullName = getFullName(row);
    const type = row?.type?.toLowerCase() || "";
    const search = searchTerm.toLowerCase();

    return fullName.includes(search) || type.includes(search);
  });

  const auditLabels = () => {
    return {
      create: "Shareholder Created",
      update: "Shareholder Updated",
      delete: "Shareholder Deleted",
    };
  };
  const handleEditClick = (rowData) => {
    setSelectedShareHolder(rowData);
  };

  useEffect(() => {
    if (selectedShareHolder) {
      const id = selectedShareHolder.id;
      navigate(`/share-holders/${id}/info`, {
        state: {
          rowData: selectedShareHolder,
        },
      });
    }
  }, [selectedShareHolder, navigate]);
  const statusColorClass = {
    Active: "active-circle-fill",
    Inactive: "inactive-circle-fill",
    Deceased: "deceased-circle-fill",
  };

  const statusOptionsTemplate = (status) => {
    return (
      <div className="flex align-items-center gap-2">
        <span
          className={`pi pi-circle-fill ${statusColorClass[status]}`}
        ></span>
        <span>{status}</span>
      </div>
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex align-items-center">
        <span
          className="pi pi-pencil ml-4 mr-3 pencil-pointer"
          onClick={() => handleEditClick(rowData)}
        ></span>
      </div>
    );
  };

  const handleSort = (event) => {
    const multiSortMeta = event.multiSortMeta || [];
    const dataToSort = [...filteredData];

    dataToSort.sort((firstRow, secondRow) => {
      for (const sortMeta of multiSortMeta) {
        const { field, order } = sortMeta;

        const firstValue =
          field === "shareholder_name"
            ? getFullName(firstRow)
            : (firstRow[field] ?? "");

        const secondValue =
          field === "shareholder_name"
            ? getFullName(secondRow)
            : (secondRow[field] ?? "");

        const comparisonResult =
          firstValue.toString().localeCompare(secondValue.toString()) * order;

        if (comparisonResult !== 0) {
          return comparisonResult;
        }
      }

      return 0;
    });

    setSortedData(dataToSort);
    setSortMeta(multiSortMeta);
  };

  useEffect(() => {
    setSortedData([...filteredData]);
  }, [data, searchTerm]);
  return (
    <>
      <Card className="gen-card h-full">
        <div className="flex align-items-center">
          <div className="flex-1">
            <div className="input-wrapper-container search__shareholder">
              <span className="pi pi-search"></span>
              <input
                type="text"
                placeholder="Search shareholders by given name, surname or shareholder type"
                className="appearance-none outline-none w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="pi pi-search"></span>
            </div>
          </div>
          <AdminOnly>
            <div className="field mb-0">
              <div className="flex justify-content-end">
                <button
                  className="add-new-contact"
                  onClick={onAddShareholderClick}
                >
                  Add Shareholder <span className="pi pi-plus ml-2"></span>
                </button>
              </div>
            </div>
          </AdminOnly>
        </div>
        <div className="formgrid grid">
          <div className="field col-12">
            <Notification
              successMessage={successMessage}
              setSuccessMessage={setSuccessMessage}
              editToastMsg={editToastMsg}
              setEditToastMsg={setEditToastMsg}
            />
          </div>
          <div className="field col-12 table-section">
            <div className="card">
              <DataTable
                value={sortedData}
                sortMode="multiple"
                tableStyle={{ minWidth: "100rem" }}
                paginator={sortedData?.length > 10}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                rows={10}
                rowsPerPageOptions={[10, 20, 30]}
                className="datatable-right-align"
                emptyMessage="No results were found"
                customSort
                onSort={handleSort}
                multiSortMeta={sortMeta}
              >
                <Column
                  header="Shareholder"
                  sortable
                  className="table-col-width"
                  sortField="shareholder_name"
                  body={(rowData) => (
                    <Link
                      className="officer__link"
                      to={`/share-holders/${rowData?.id}/info`}
                    >
                      {[rowData?.name, rowData?.other_name, rowData?.last_name]
                        .filter(Boolean)
                        .join(" ") || rowData?.legal_name}
                    </Link>
                  )}
                ></Column>
                <Column
                  field="type"
                  header="Type"
                  sortable
                  className="table-col-width"
                ></Column>
                <Column
                  header="Total Number of Shares"
                  className="table-col-width"
                  body={(field) => field?.totalShares}
                />
                <Column
                  header="Share Value"
                  className="table-col-width"
                  body={(field) => {
                    const currencyCode = field?.currency;
                    const currency = currencyList.find(
                      (c) => c.value === currencyCode,
                    );
                    const symbol = currency?.symbol;
                    return (
                      <span>
                        {symbol} {field?.share_value}
                      </span>
                    );
                  }}
                />
                <Column
                  header="Share %"
                  className="table-col-width"
                  body={(field) => field?.share_percentage + "%"}
                />
                <Column
                  header="Share Classes"
                  className="table-col-width"
                  body={(field) => field?.share_classes?.join(",")}
                />
                <Column
                  header="Last Update"
                  sortable
                  field="updatedAt"
                  className="table-col-width"
                  body={(field) =>
                    field.lastUpdateDate &&
                    format(field.lastUpdateDate, "MMMM do, yyyy")
                  }
                />
                <Column
                  header="Status"
                  className="table-col-width"
                  body={(field) =>
                    field?.status ? statusOptionsTemplate(field?.status) : null
                  }
                />
              </DataTable>
            </div>
          </div>
        </div>
      </Card>
      {isAuditSidebarOpen && (
        <AuditSidebar
          heading="Shareholders"
          endpoint={API_ROUTES.FETCH_SHAREHOLDERS_AUDIT}
          id={entityId}
          objectType="entity"
          endType="SHAREHOLDERS"
          setSidebarToggle={setIsAuditSidebarOpen}
          sidebarToggle={isAuditSidebarOpen}
          labels={auditLabels}
        />
      )}
    </>
  );
};

export default ShareHoldersTable;
